import { TextsToSpeechesDocument } from "@/generated/graphql";
import { AUDIO_PITCH_LIMIT, AUDIO_SPEED_LIMIT } from "@/lib/constants/slides";
import { toastAiCredits } from "@/lib/utils/aiCredits";
import {
  type MediaUsageContext,
  MediaUsageContextSchema,
  type WordBoundary,
} from "@ai-teach/types";
import { Button, Modal } from "@ai-teach/ui-web";
import { AZURE_VOICES } from "@ai-teach/utils";
import { useMutation } from "@apollo/client/react";
import { useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";
import toast from "react-hot-toast";
import { FaChevronDown, FaPause, FaPlay } from "react-icons/fa";

interface VoiceSettingsModalProps {
  onClose: () => void;
  audioData: {
    id: string | null;
    filename: string | null;
    src: string | null;
  };
  text: string | null;
  voice: string;
  slideId: string;
  usageContext?: MediaUsageContext;
  onSave: (data: {
    id: string | null;
    filename: string | null;
    src: string | null;
    voice: string;
    wordBoundaries?: WordBoundary[] | null;
  }) => void;
}

interface AudioState {
  id: string | null;
  filename: string | null;
  src: string | null;
  voice: string;
  speed: number;
  pitch: number;
  isPlaying: boolean;
  isLoading: boolean;
  isSaving: boolean;
  voiceDropdownOpen: boolean;
}

const SPEED_LIMIT = AUDIO_SPEED_LIMIT;
const PITCH_LIMIT = AUDIO_PITCH_LIMIT;

const defaultAudioState = (
  audioData: VoiceSettingsModalProps["audioData"],
  voice: string
): AudioState => ({
  id: audioData.id,
  filename: audioData.filename,
  src: audioData.src,
  voice,
  speed: SPEED_LIMIT.default,
  pitch: PITCH_LIMIT.default,
  isPlaying: false,
  isLoading: false,
  isSaving: false,
  voiceDropdownOpen: false,
});

export default function VoiceSettingsModal({
  onClose,
  audioData,
  text,
  voice,
  slideId,
  usageContext = MediaUsageContextSchema.Values.slideEditor,
  onSave,
}: VoiceSettingsModalProps) {
  const [audioA, setAudioA] = useState<AudioState>(() => defaultAudioState(audioData, voice));
  const [audioB, setAudioB] = useState<AudioState>(() => defaultAudioState(audioData, voice));

  const audioRefA = useRef<HTMLAudioElement | null>(null);
  const audioRefB = useRef<HTMLAudioElement | null>(null);
  const voiceDropdownRefA = useRef<HTMLDivElement | null>(null);
  const voiceDropdownRefB = useRef<HTMLDivElement | null>(null);

  const [textsToSpeechesMutation] = useMutation(TextsToSpeechesDocument);

  useEffect(() => {
    setAudioA(defaultAudioState(audioData, voice));
    setAudioB(defaultAudioState(audioData, voice));
  }, [audioData, voice]);

  useEffect(() => {
    if (!audioA.voiceDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (voiceDropdownRefA.current && !voiceDropdownRefA.current.contains(e.target as Node)) {
        setAudioA((prev) => ({ ...prev, voiceDropdownOpen: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [audioA.voiceDropdownOpen]);

  useEffect(() => {
    if (!audioB.voiceDropdownOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (voiceDropdownRefB.current && !voiceDropdownRefB.current.contains(e.target as Node)) {
        setAudioB((prev) => ({ ...prev, voiceDropdownOpen: false }));
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [audioB.voiceDropdownOpen]);

  const getNewAudio = async ({
    pitch,
    speed,
    voice: voiceName,
  }: {
    pitch: number;
    speed: number;
    voice: string;
  }) => {
    if (!text) return;

    try {
      const result = await textsToSpeechesMutation({
        variables: {
          texts: [text],
          voice: voiceName,
          slideId,
          usageContext,
          speed,
          pitch,
        },
      });

      return result.data?.textsToSpeeches ?? [];
    } catch (error) {
      if (!toastAiCredits(error, "generatingAudioToast")) {
        toast.error("Failed to generate audio!", { id: "generatingAudioToast" });
      }
      return;
    }
  };

  const handleAudioPlay = async (target: "A" | "B") => {
    const a = audioRefA.current;
    const b = audioRefB.current;

    if (!a || !b) return;

    const audio = target === "A" ? audioA : audioB;
    const current = target === "A" ? a : b;
    const other = target === "A" ? b : a;
    const setAudio = target === "A" ? setAudioA : setAudioB;
    const setOther = target === "A" ? setAudioB : setAudioA;

    other.pause();
    other.currentTime = 0;
    setOther((prev) => ({ ...prev, isPlaying: false }));

    if (!current.paused) {
      current.pause();
      return;
    }

    current.play().catch(() => {});
    current.pause();
    current.currentTime = 0;

    setAudio((prev) => ({ ...prev, isLoading: true }));

    const newAudio = await getNewAudio({
      pitch: audio.pitch,
      speed: audio.speed / 100,
      voice: audio.voice,
    });

    if (!newAudio?.[0]?.src) {
      setAudio((prev) => ({ ...prev, isLoading: false }));
      return;
    }

    const newSrc = newAudio[0].src;

    // flushSync forces React to reconcile synchronously, setting element.src = newSrc
    // BEFORE play() is called. Without this, React's async reconciliation re-assigns
    // .src after play() starts, triggering a second load that aborts playback (AbortError).
    flushSync(() => {
      setAudio((prev) => ({
        ...prev,
        id: newAudio?.[0]?.id ?? prev.id,
        filename: newAudio?.[0]?.filename ?? prev.filename,
        src: newSrc,
        isLoading: false,
      }));
    });

    current.currentTime = 0;
    current.play().catch(() => {});
  };

  const handleSave = async (target: "A" | "B") => {
    const audio = target === "A" ? audioA : audioB;
    const setAudio = target === "A" ? setAudioA : setAudioB;

    setAudio((prev) => ({ ...prev, isSaving: true }));

    const newAudio = await getNewAudio({
      pitch: audio.pitch,
      speed: audio.speed / 100,
      voice: audio.voice,
    });

    if (!newAudio?.[0]?.src) {
      setAudio((prev) => ({ ...prev, isSaving: false }));
      return;
    }

    onSave({
      id: newAudio[0].id ?? audio.id,
      filename: newAudio[0].filename ?? audio.filename,
      src: newAudio[0].src,
      voice: audio.voice,
      wordBoundaries: newAudio[0].wordBoundaries ?? null,
    });
    onClose();
  };

  return (
    <Modal title="Voice Settings" isOpen={true} onClose={onClose} size="xl">
      <h1 className="font-bold uppercase tracking-wider p-2">Compare A/B voice</h1>
      <div className="flex gap-4 pt-1">
        {(["A", "B"] as const).map((target) => {
          const audio = target === "A" ? audioA : audioB;
          const audioRef = target === "A" ? audioRefA : audioRefB;
          const voiceDropdownRef = target === "A" ? voiceDropdownRefA : voiceDropdownRefB;
          const setAudio = target === "A" ? setAudioA : setAudioB;
          const isDisabled =
            audioA.isLoading || audioB.isLoading || audioA.isSaving || audioB.isSaving;
          const selectedVoice =
            AZURE_VOICES.find((v) => v.value === audio.voice) ?? AZURE_VOICES[0];

          return (
            <div
              key={target}
              className="flex-1 rounded-lg border border-border bg-surface p-4 flex flex-col gap-4"
            >
              <audio
                ref={audioRef}
                src={audio.src ?? undefined}
                preload="metadata"
                onPlay={() => setAudio((prev) => ({ ...prev, isPlaying: true }))}
                onPause={() => setAudio((prev) => ({ ...prev, isPlaying: false }))}
                onEnded={() => setAudio((prev) => ({ ...prev, isPlaying: false }))}
              >
                <track kind="captions" srcLang="en" default />
              </audio>

              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">
                Setting {target}
              </p>

              <div className="flex flex-col gap-3">
                {/* Voice */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                    Voice
                  </label>
                  <div ref={voiceDropdownRef} className="relative">
                    <button
                      type="button"
                      onClick={() =>
                        setAudio((prev) => ({
                          ...prev,
                          voiceDropdownOpen: !prev.voiceDropdownOpen,
                        }))
                      }
                      className="w-full h-8 pl-2.5 pr-2 flex items-center gap-1.5 rounded-md border border-slate-200 bg-white hover:bg-primary-50 hover:border-primary-300 transition-all duration-200 shadow-sm"
                    >
                      <span className="flex-1 min-w-0 text-left flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-slate-800 truncate leading-none">
                          {selectedVoice.label}
                        </span>
                        <span className="text-[9px] text-slate-400 truncate leading-none">
                          {selectedVoice.detail}
                        </span>
                      </span>
                      <FaChevronDown
                        className={`text-slate-400 shrink-0 transition-transform duration-200 ${audio.voiceDropdownOpen ? "rotate-180" : ""}`}
                        style={{ fontSize: "8px" }}
                      />
                    </button>

                    {audio.voiceDropdownOpen && (
                      <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden">
                        {AZURE_VOICES.map((v) => (
                          <button
                            key={v.value}
                            type="button"
                            onClick={() =>
                              setAudio((prev) => ({
                                ...prev,
                                voice: v.value,
                                voiceDropdownOpen: false,
                              }))
                            }
                            className={`w-full px-3 py-1.5 flex items-center gap-2 text-left transition-colors ${
                              v.value === audio.voice
                                ? "bg-primary-50 text-primary-700"
                                : "hover:bg-slate-50 text-slate-700"
                            }`}
                          >
                            <span className="flex-1 min-w-0">
                              <span className="block text-[11px] font-semibold truncate">
                                {v.label}
                              </span>
                              <span className="block text-[9px] text-slate-400 truncate">
                                {v.detail}
                              </span>
                            </span>
                            {v.value === audio.voice && (
                              <span className="w-1.5 h-1.5 rounded-full bg-primary-500 shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Speed */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Speed
                    </label>
                    <span className="text-xs font-semibold text-slate-600 tabular-nums">
                      {audio.speed / 100}x
                    </span>
                  </div>
                  <input
                    type="range"
                    min={SPEED_LIMIT.min}
                    max={SPEED_LIMIT.max}
                    value={audio.speed}
                    onChange={(e) =>
                      setAudio((prev) => ({ ...prev, speed: Number(e.target.value) }))
                    }
                    className="w-full accent-primary-500 cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400">
                    <span>{SPEED_LIMIT.min / 100}x</span>
                    <span>{SPEED_LIMIT.max / 100}x</span>
                  </div>
                </div>

                {/* Pitch */}
                <div className="flex flex-col gap-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      Pitch
                    </label>
                    <span className="text-xs font-semibold text-slate-600 tabular-nums">
                      {audio.pitch > 0 ? `+${audio.pitch}` : audio.pitch}
                    </span>
                  </div>
                  <input
                    type="range"
                    min={PITCH_LIMIT.min}
                    max={PITCH_LIMIT.max}
                    value={audio.pitch}
                    onChange={(e) =>
                      setAudio((prev) => ({ ...prev, pitch: Number(e.target.value) }))
                    }
                    className="w-full accent-primary-500 cursor-pointer"
                  />
                  <div className="flex justify-between text-[10px] text-slate-400">
                    <span>{PITCH_LIMIT.min}</span>
                    <span>{PITCH_LIMIT.default}</span>
                    <span>+{PITCH_LIMIT.max}</span>
                  </div>
                </div>
              </div>

              <Button
                onClick={() => handleAudioPlay(target)}
                disabled={isDisabled}
                variant={audio.isPlaying ? "danger" : "primary"}
                className="w-full mt-auto"
              >
                {audio.isLoading ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </span>
                ) : audio.isPlaying ? (
                  <span className="flex items-center justify-center gap-2">
                    <FaPause /> Pause {target}
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <FaPlay /> Play {target}
                  </span>
                )}
              </Button>

              <Button
                onClick={() => handleSave(target)}
                disabled={isDisabled}
                className="w-full"
                variant="secondary"
              >
                {audio.isSaving ? (
                  <span className="flex items-center justify-center gap-2">
                    <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    Applying...
                  </span>
                ) : (
                  `Apply ${target}`
                )}
              </Button>
            </div>
          );
        })}
      </div>
    </Modal>
  );
}
